import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import client, { endpoints, errText } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import { money, when, FREQUENCY_LABEL } from '../lib/format';

// The board: today's jobs, and the queues where a job is waiting on a person.
// Each count links straight to the filtered list, because a number nobody can act
// on is decoration.
export default function Home() {
  const { user } = useSelector((s) => s.auth);
  const [board, setBoard] = useState(null);
  const [setup, setSetup] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!user) return;
    client.get(endpoints.bookings.board)
      .then((r) => setBoard(r.data))
      .catch((e) => setErr(errText(e)));
    // The setup checklist is admin-only; a dispatcher simply does not see it.
    if (user.role === 'admin') {
      client.get(endpoints.admin.setup).then((r) => setSetup(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user) {
    return (
      <section className="section narrow">
        <h1>Squeakky Ops</h1>
        <p className="muted">The operations console for Squeakky — House of Service. Sign in to continue.</p>
        <Link to="/login" className="btn-primary">Sign in</Link>
      </section>
    );
  }

  const c = board?.counts || {};
  const queues = [
    { key: 'awaitingDeposit', label: 'Awaiting deposit', to: '/ops/bookings?status=awaiting_deposit', hint: 'Slot held, money not in yet' },
    { key: 'unassigned', label: 'Needs a team', to: '/ops/bookings?status=confirmed&unassigned=1', hint: 'Confirmed but nobody dispatched' },
    { key: 'qualityCheck', label: 'Quality check', to: '/ops/bookings?status=quality_check', hint: 'Work done, waiting on sign-off' },
    { key: 'balanceDue', label: 'Balance due', to: '/ops/bookings?status=completed', hint: 'Completed, balance unpaid' },
    { key: 'followUps', label: 'Follow-ups due', to: '/ops/bookings?status=completed', hint: 'Check-in after service' },
  ];

  return (
    <section className="section">
      <h1>Today</h1>
      {err && <p className="alert alert-err">{err}</p>}

      {setup && !setup.ready && (
        <div className="card card-warn">
          <h2>Setup not finished</h2>
          <p className="muted">
            These are the things only you can supply. Each one names exactly what stays switched off until it is done —
            none of them is a bug.
          </p>
          <ul className="setup-list">
            {setup.steps.filter((s) => !s.done).map((s) => (
              <li key={s.key}>
                <strong>{s.label}</strong> <span className="muted">— {s.detail}</span>
                <div className="setup-blocks">{s.blocks}</div>
              </li>
            ))}
          </ul>
          <p className="muted small">
            Prices are set on <Link to="/ops/services">Services</Link>, teams on <Link to="/ops/teams">Teams</Link>.
          </p>
        </div>
      )}

      <div className="queue-grid">
        {queues.map((q) => (
          <Link key={q.key} to={q.to} className={`queue ${c[q.key] ? 'queue-active' : ''}`}>
            <strong>{c[q.key] ?? '—'}</strong>
            <span>{q.label}</span>
            <em>{q.hint}</em>
          </Link>
        ))}
      </div>

      <div className="section-head">
        <h2>Jobs today {board?.today ? <span className="muted">({board.today})</span> : null}</h2>
        <Link to="/ops/bookings">All bookings →</Link>
      </div>

      {!board && !err && <p className="muted">Loading…</p>}
      {board && board.todays.length === 0 && <p className="muted">Nothing scheduled today.</p>}
      {board && board.todays.length > 0 && (
        <table className="table">
          <thead>
            <tr><th>Time</th><th>Reference</th><th>Service</th><th>Customer</th><th>City</th><th>Team</th><th>Status</th><th>Money</th></tr>
          </thead>
          <tbody>
            {board.todays.map((b) => (
              <tr key={b._id}>
                <td>{b.time}</td>
                <td><Link to={`/ops/bookings/${b.reference}`}>{b.reference}</Link></td>
                <td>{b.serviceName}<div className="muted small">{FREQUENCY_LABEL[b.frequency] || b.frequency}</div></td>
                <td>{b.contact?.name}<div className="muted small">{b.contact?.phone}</div></td>
                <td>{b.city}</td>
                <td>{b.team?.name || <span className="muted">unassigned</span>}</td>
                <td><StatusBadge status={b.status} /></td>
                <td>
                  {money(b.money?.totalMinor, b.money?.currency)}
                  <div className="muted small">
                    {b.money?.depositPaid ? 'deposit paid' : 'deposit due'}
                    {b.money?.balancePaid ? ' · balance paid' : ''}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
